import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useScopedQueryKey } from '../../../hooks/useScopedQueryKey';
import {
  Avatar,
  Button,
  Callout,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  IconPlus,
  IconUsers,
  Input,
  QueryGate,
  Radio,
  Reason,
  RowMenu,
  SavedNote,
  SectionBody,
  SectionHead,
  StatusLabel,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TwoLine,
  useSavedAt,
} from '../../../ui/v2';
import { api, apiEndpoints } from '../../../utils/apiService';
import {
  type AddMemberForm,
  type ApiStoreStaff,
  type ApiStoreUser,
  type EditMemberForm,
  type FormErrors,
  type TeamMember,
  type TeamRole,
  EMPTY_ADD_FORM,
  MIN_PASSWORD,
  ROLE_DESCRIPTION,
  TEAM_ROLES,
  addPayload,
  apiErrorMessage,
  editChanges,
  editFormFrom,
  joinTeam,
  lockedReason,
  memberName,
  roleCounts,
  validateAddForm,
  validateEditForm,
} from './teamModel';

/**
 * Store Info › Team — who can sign in to this store's dashboard.
 *
 * Replaces the standalone Add staff page. One row per member; Add and Edit
 * are dialogs, Remove confirms. Removing takes someone off this store only;
 * their account and any other stores stay. The backend refuses removing or
 * demoting yourself or the last Owner, and the row menu and role radios say
 * so up front.
 */

async function readJson(response: Response | null) {
  return response ? response.json().catch(() => ({})) : {};
}

async function fetchTeam(): Promise<TeamMember[]> {
  const [users, staff] = await Promise.all([
    api.get(apiEndpoints.getStoreUsers()),
    api.get(apiEndpoints.getStoreStaff()),
  ]);
  if (!users?.ok || !staff?.ok)
    throw new Error(
      apiErrorMessage(
        await readJson(users?.ok ? staff : users),
        'The team could not be loaded.',
      ),
    );
  return joinTeam(
    (await users.json()) as ApiStoreUser[],
    (await staff.json()) as ApiStoreStaff[],
  );
}

async function send(
  request: Promise<Response | null>,
  fallback: string,
): Promise<void> {
  const response = await request;
  if (!response?.ok)
    throw new Error(apiErrorMessage(await readJson(response), fallback));
}

const errorText = (e: unknown, fallback: string) =>
  e instanceof Error && e.message ? e.message : fallback;

const TeamSection: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = useScopedQueryKey('store-team');
  const team = useQuery({ queryKey, queryFn: fetchTeam });
  const members = team.data ?? [];

  const [adding, setAdding] = useState<AddMemberForm | null>(null);
  const [editing, setEditing] = useState<{
    member: TeamMember;
    form: EditMemberForm;
  } | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    FormErrors<AddMemberForm & EditMemberForm>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');

  const reset = () => {
    setError(null);
    setFieldErrors({});
  };
  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey });
    setSavedText(message);
    markSaved();
  };

  const openAdd = () => {
    reset();
    setAdding(EMPTY_ADD_FORM);
  };
  const openEdit = (member: TeamMember) => {
    reset();
    setEditing({ member, form: editFormFrom(member) });
  };
  const openRemove = (member: TeamMember) => {
    reset();
    setRemoving(member);
  };

  const add = async () => {
    if (!adding) return;
    const errors = validateAddForm(
      adding,
      members.map((m) => m.email),
    );
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setBusy(true);
    setError(null);
    try {
      await send(
        api.post(apiEndpoints.createStaff(), addPayload(adding)),
        'The member could not be added.',
      );
      setAdding(null);
      await done(`${adding.firstName.trim()} added.`);
    } catch (e) {
      setError(errorText(e, 'The member could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const errors = validateEditForm(editing.form);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    const { member } = editing;
    const changes = editChanges(member, editing.form);
    if (!changes.name && !changes.role) return setEditing(null);
    setBusy(true);
    setError(null);
    try {
      if (changes.name)
        await send(
          api.patch(apiEndpoints.updateStoreUser(member.userId), changes.name),
          'The name could not be saved.',
        );
      if (changes.role) {
        if (member.staffId == null)
          throw new Error(
            'This member’s role can’t be changed here. Reload the page and try again.',
          );
        await send(
          api.put(apiEndpoints.updateStoreStaff(member.staffId), {
            role: changes.role,
          }),
          'The role could not be saved.',
        );
      }
      setEditing(null);
      await done('Member updated.');
    } catch (e) {
      // A saved name stays saved even if the role change fails.
      if (changes.name) void queryClient.invalidateQueries({ queryKey });
      setError(errorText(e, 'The changes could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      await send(
        api.delete(apiEndpoints.updateStoreUser(removing.userId)),
        'The member could not be removed.',
      );
      const name = memberName(removing) || removing.email;
      setRemoving(null);
      await done(`${name} removed.`);
    } catch (e) {
      setError(errorText(e, 'The member could not be removed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionBody measure="full">
      <SectionHead
        title="Team"
        action={
          <>
            <SavedNote when={savedAt}>{savedText}</SavedNote>
            <Button
              size="sm"
              variant="primary"
              leading={<IconPlus size={14} />}
              onClick={openAdd}
              disabled={team.status !== 'success'}
            >
              Add member
            </Button>
          </>
        }
      />
      <div className="mt-6">
        <QueryGate
          query={team}
          errorTitle="The team could not be loaded."
          skeleton={
            <Table fixed>
              <tbody>
                <TableSkeletonRows rows={3} cols={4} />
              </tbody>
            </Table>
          }
        >
          {(rows) =>
            rows.length === 0 ? (
              <EmptyState
                icon={<IconUsers />}
                title="No one else is on the team yet"
                description="Add the people who run this store so they can sign in and see calls, orders and reservations."
                action={
                  <Button size="sm" variant="secondary" onClick={openAdd}>
                    Add member
                  </Button>
                }
              />
            ) : (
              <>
                <Reason className="mb-2">{roleCounts(rows)}</Reason>
                <Table
                  fixed
                  aria-label="Team members"
                  className="-mx-4 w-[calc(100%+32px)] lg:-mx-6 lg:w-[calc(100%+48px)]"
                >
                  <THead>
                    <tr>
                      <TH>Member</TH>
                      <TH width={112}>Role</TH>
                      <TH width={112}>Status</TH>
                      <TH width={56} align="right">
                        <span className="sr-only">Actions</span>
                      </TH>
                    </tr>
                  </THead>
                  <TBody>
                    {rows.map((m) => {
                      const name = memberName(m);
                      const isYou = m.userId === user?.id;
                      return (
                        <TR
                          key={m.userId}
                          onActivate={() => openEdit(m)}
                          className={m.isActive ? undefined : 'text-v2-fg-3'}
                        >
                          <TD truncate className="!h-11">
                            <span className="flex min-w-0 items-center gap-2.5">
                              <Avatar
                                name={name || m.email}
                                identity={m.userId}
                                hue="auto"
                              />
                              <TwoLine
                                primary={
                                  <>
                                    {name || m.email}
                                    {isYou && (
                                      <Tag className="ml-1.5 align-middle">
                                        You
                                      </Tag>
                                    )}
                                  </>
                                }
                                secondary={name ? m.email : undefined}
                              />
                            </span>
                          </TD>
                          <TD>
                            <Tag>{m.role}</Tag>
                          </TD>
                          <TD>
                            <StatusLabel
                              size="sm"
                              tone={m.isActive ? 'success' : 'neutral'}
                              hollow={!m.isActive}
                            >
                              {m.isActive ? 'Active' : 'Inactive'}
                            </StatusLabel>
                          </TD>
                          <TD align="right">
                            <RowMenu
                              label={`Actions for ${name || m.email}`}
                              onEdit={() => openEdit(m)}
                              onDelete={() => openRemove(m)}
                              deleteLabel="Remove…"
                              deleteDisabledReason={lockedReason(
                                m,
                                rows,
                                user?.id,
                                'remove',
                              )}
                            />
                          </TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              </>
            )
          }
        </QueryGate>
      </div>

      <AddMemberDialog
        form={adding}
        errors={fieldErrors}
        error={error}
        busy={busy}
        onChange={(form) => setAdding(form)}
        onClose={() => !busy && setAdding(null)}
        onSave={add}
      />

      <EditMemberDialog
        editing={editing}
        roleLockedReason={
          editing
            ? lockedReason(editing.member, members, user?.id, 'role')
            : null
        }
        errors={fieldErrors}
        error={error}
        busy={busy}
        onChange={(form) => editing && setEditing({ ...editing, form })}
        onClose={() => !busy && setEditing(null)}
        onSave={saveEdit}
      />

      <ConfirmDialog
        open={removing !== null}
        onClose={() => !busy && setRemoving(null)}
        onConfirm={remove}
        title={`Remove ${removing ? memberName(removing) || removing.email : ''}?`}
        confirmLabel="Remove"
        busy={busy}
        consequence="They can no longer sign in to this store. Their account, and access to any other stores, stays as it is."
      >
        {error && <Callout tone="danger">{error}</Callout>}
      </ConfirmDialog>
    </SectionBody>
  );
};

const RoleChoice: React.FC<{
  value: TeamRole;
  onChange: (role: TeamRole) => void;
  disabledReason?: string | null;
}> = ({ value, onChange, disabledReason }) => (
  <fieldset>
    <legend className="text-v2-12 font-medium text-v2-fg-2">Role</legend>
    <div
      role="radiogroup"
      aria-label="Role"
      className="mt-1.5 flex flex-col gap-2.5"
    >
      {TEAM_ROLES.map((role) => (
        <Radio
          key={role}
          name="team-role"
          label={role}
          description={ROLE_DESCRIPTION[role]}
          checked={value === role}
          disabled={!!disabledReason}
          onChange={() => onChange(role)}
        />
      ))}
    </div>
    {disabledReason && <Reason className="mt-2">{disabledReason}</Reason>}
  </fieldset>
);

const AddMemberDialog: React.FC<{
  form: AddMemberForm | null;
  errors: FormErrors<AddMemberForm>;
  error: string | null;
  busy: boolean;
  onChange: (form: AddMemberForm) => void;
  onClose: () => void;
  onSave: () => void;
}> = ({ form, errors, error, busy, onChange, onClose, onSave }) => {
  const set = (patch: Partial<AddMemberForm>) =>
    form && onChange({ ...form, ...patch });
  return (
    <Dialog
      open={form !== null}
      onClose={onClose}
      dismissible={!busy}
      title="Add member"
      description="They sign in with this email and password. Share the password with them; they can change it with Forgot password."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={busy}>
            Add member
          </Button>
        </>
      }
    >
      {form && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          noValidate
        >
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field label="First name" error={errors.firstName}>
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  data-autofocus="true"
                  value={form.firstName}
                  invalid={!!errors.firstName}
                  onChange={(e) => set({ firstName: e.target.value })}
                />
              )}
            </Field>
            <Field label="Last name" error={errors.lastName}>
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  value={form.lastName}
                  invalid={!!errors.lastName}
                  onChange={(e) => set({ lastName: e.target.value })}
                />
              )}
            </Field>
            <Field label="Email" error={errors.email} className="sm:col-span-2">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  autoComplete="off"
                  placeholder="name@example.com"
                  value={form.email}
                  invalid={!!errors.email}
                  onChange={(e) => set({ email: e.target.value })}
                />
              )}
            </Field>
            <Field
              label="Password"
              hint={`At least ${MIN_PASSWORD} characters.`}
              error={errors.password}
              className="sm:col-span-2"
            >
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  invalid={!!errors.password}
                  onChange={(e) => set({ password: e.target.value })}
                />
              )}
            </Field>
          </div>
          <RoleChoice value={form.role} onChange={(role) => set({ role })} />
          {error && <Callout tone="danger">{error}</Callout>}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      )}
    </Dialog>
  );
};

const EditMemberDialog: React.FC<{
  editing: { member: TeamMember; form: EditMemberForm } | null;
  roleLockedReason: string | null;
  errors: FormErrors<EditMemberForm>;
  error: string | null;
  busy: boolean;
  onChange: (form: EditMemberForm) => void;
  onClose: () => void;
  onSave: () => void;
}> = ({
  editing,
  roleLockedReason,
  errors,
  error,
  busy,
  onChange,
  onClose,
  onSave,
}) => {
  const form = editing?.form;
  const set = (patch: Partial<EditMemberForm>) =>
    form && onChange({ ...form, ...patch });
  return (
    <Dialog
      open={editing !== null}
      onClose={onClose}
      dismissible={!busy}
      title="Edit member"
      description="Their email and password belong to their account, so only they can change them."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={busy}>
            Save
          </Button>
        </>
      }
    >
      {editing && form && (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          noValidate
        >
          <div className="flex items-center gap-2.5 border-b border-v2-line pb-3">
            <Avatar
              name={memberName(editing.member) || editing.member.email}
              identity={editing.member.userId}
              hue="auto"
              size="md"
            />
            <TwoLine
              primary={memberName(editing.member) || editing.member.email}
              secondary={editing.member.email}
            />
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field label="First name" error={errors.firstName}>
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  data-autofocus="true"
                  value={form.firstName}
                  invalid={!!errors.firstName}
                  onChange={(e) => set({ firstName: e.target.value })}
                />
              )}
            </Field>
            <Field label="Last name" error={errors.lastName}>
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  value={form.lastName}
                  invalid={!!errors.lastName}
                  onChange={(e) => set({ lastName: e.target.value })}
                />
              )}
            </Field>
          </div>
          <RoleChoice
            value={form.role}
            onChange={(role) => set({ role })}
            disabledReason={roleLockedReason}
          />
          {error && <Callout tone="danger">{error}</Callout>}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      )}
    </Dialog>
  );
};

export default TeamSection;
